import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import './AnalyticsDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function AnalyticsDashboard() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [insights, setInsights] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalyticsData();
  }, [token]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const [dashRes, insightsRes] = await Promise.all([
        axios.get(`${API_BASE}/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/analytics/insights`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDashboard(dashRes.data.dashboard);
      setInsights(insightsRes.data);
    } catch (error) {
      toast.error('Error loading analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="analytics-header">
        <h1>📊 Analytics Dashboard</h1>
        <p>Email deliverability trends, insights, and recommendations</p>
      </div>

      {/* Tabs */}
      <div className="analytics-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Trends
        </button>
        <button
          className={`tab-button ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          Insights
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="analytics-section">
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-number">{dashboard.key_metrics.total_updates || 0}</div>
              <div className="metric-label">Total Updates</div>
            </div>
            <div className="metric-card">
              <div className="metric-number">{dashboard.key_metrics.new_this_month || 0}</div>
              <div className="metric-label">New This Month</div>
            </div>
            <div className="metric-card">
              <div className="metric-number">{dashboard.key_metrics.critical_count || 0}</div>
              <div className="metric-label" style={{ color: '#ffd4d4' }}>Critical Issues</div>
            </div>
            <div className="metric-card">
              <div className="metric-number">{dashboard.key_metrics.isps_affected || 0}</div>
              <div className="metric-label">ISPs Affected</div>
            </div>
          </div>

          {/* Severity Distribution */}
          <div className="analytics-row">
            <div className="analytics-card full-width">
              <h3>Severity Distribution</h3>
              {dashboard.severity_distribution && (
                <div className="severity-chart">
                  {dashboard.severity_distribution.map((item) => (
                    <div key={item.severity} className="severity-bar-container">
                      <label>{item.severity}</label>
                      <div className="severity-bar" style={{
                        background: item.severity === 'critical' ? '#ff3333' : 
                                   item.severity === 'high' ? '#ffa500' :
                                   item.severity === 'medium' ? '#ffd700' : '#667eea'
                      }}>
                        <div style={{
                          width: `${(item.count / Math.max(...dashboard.severity_distribution.map(x => x.count))) * 100}%`,
                          height: '100%',
                          background: 'inherit',
                          borderRadius: '4px'
                        }}></div>
                        <span>{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ISP Impact Analysis */}
          <div className="analytics-card full-width">
            <h3>ISP Impact Analysis (Last 30 Days)</h3>
            {dashboard.isp_impact && (
              <div className="isp-impact-table">
                <table>
                  <thead>
                    <tr>
                      <th>ISP</th>
                      <th>Updates</th>
                      <th>Critical</th>
                      <th>High</th>
                      <th>Medium</th>
                      <th>Impact Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.isp_impact.map((isp) => (
                      <tr key={isp.isp}>
                        <td><strong>{isp.isp}</strong></td>
                        <td>{isp.update_count}</td>
                        <td style={{ color: '#ff3333' }}>{isp.critical_count || 0}</td>
                        <td style={{ color: '#ffa500' }}>{isp.high_count || 0}</td>
                        <td style={{ color: '#ffd700' }}>{isp.medium_count || 0}</td>
                        <td>
                          <div className="score-badge" style={{
                            background: isp.impact_score > 70 ? '#ff3333' : 
                                       isp.impact_score > 40 ? '#ffa500' : '#2e7d32'
                          }}>
                            {isp.impact_score}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Technology Adoption Trends */}
          <div className="analytics-card full-width">
            <h3>Technology Adoption Trends</h3>
            {dashboard.technology_trends && (
              <div className="tech-trends">
                {dashboard.technology_trends.slice(0, 8).map((tech) => (
                  <div key={tech.technology} className="tech-item">
                    <label>{tech.technology}</label>
                    <div className="tech-bar-container">
                      <div className="tech-bar" style={{
                        width: `${(tech.adoption_percentage || 0)}%`,
                        background: 'linear-gradient(90deg, #667eea, #764ba2)'
                      }}></div>
                    </div>
                    <span>{tech.adoption_percentage?.toFixed(1) || 0}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Most Impactful Changes */}
          <div className="analytics-card full-width">
            <h3>Most Impactful Changes (Last 30 Days)</h3>
            {dashboard.most_impactful && (
              <div className="impactful-list">
                {dashboard.most_impactful.slice(0, 5).map((change, idx) => (
                  <div key={idx} className="impactful-item">
                    <div className="impact-rank">#{idx + 1}</div>
                    <div className="impact-details">
                      <h4>{change.title}</h4>
                      <p>{change.summary}</p>
                      <div className="impact-tags">
                        <span className="tag isp">{change.isp}</span>
                        <span className={`tag severity ${change.severity}`}>
                          {change.severity}
                        </span>
                        {change.category && (
                          <span className="tag category">{change.category}</span>
                        )}
                      </div>
                    </div>
                    <div className="impact-score">
                      <strong>{change.impact_score}</strong>
                      <small>score</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && dashboard && (
        <div className="analytics-section">
          {/* Trending Topics */}
          <div className="analytics-card full-width">
            <h3>🔥 Trending Topics</h3>
            {dashboard.trending_topics && (
              <div className="trending-grid">
                {dashboard.trending_topics.map((topic) => (
                  <div key={topic.topic} className="trending-card">
                    <div className="trend-count">{topic.mention_count}</div>
                    <div className="trend-topic">{topic.topic}</div>
                    {topic.trend_direction && (
                      <div style={{
                        color: topic.trend_direction === 'increasing' ? '#2e7d32' : '#ff3333',
                        fontSize: '12px'
                      }}>
                        {topic.trend_direction === 'increasing' ? '📈' : '📉'} 
                        {' '}{topic.trend_percentage?.toFixed(1) || 0}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timeline View */}
          <div className="analytics-card full-width">
            <h3>📅 Change Timeline (Last 6 Months)</h3>
            {dashboard.timeline && (
              <div className="timeline">
                {dashboard.timeline.map((entry) => (
                  <div key={entry.date} className="timeline-entry">
                    <div className="timeline-date">
                      {new Date(entry.date).toLocaleDateString()}
                    </div>
                    <div className="timeline-count">
                      <strong>{entry.update_count}</strong> updates
                    </div>
                    {entry.highlights && (
                      <div className="timeline-highlights">
                        {entry.highlights.slice(0, 2).map((h, idx) => (
                          <small key={idx}>• {h}</small>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Implementation Difficulty Distribution */}
          <div className="analytics-card full-width">
            <h3>Implementation Difficulty</h3>
            {dashboard.implementation_difficulty && (
              <div className="difficulty-distribution">
                {dashboard.implementation_difficulty.map((item) => (
                  <div key={item.difficulty} className="difficulty-item">
                    <label>{item.difficulty}</label>
                    <div className="difficulty-bar">
                      <div style={{
                        width: `${(item.percentage || 0)}%`,
                        height: '100%',
                        background: item.difficulty === 'easy' ? '#2e7d32' :
                                   item.difficulty === 'medium' ? '#ffa500' : '#ff3333'
                      }}></div>
                    </div>
                    <span>{item.count} items ({item.percentage?.toFixed(0) || 0}%)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Insights Tab */}
      {activeTab === 'insights' && insights && (
        <div className="analytics-section">
          {/* Key Recommendations */}
          <div className="analytics-card full-width">
            <h3>💡 Recommended Actions</h3>
            {insights.recommendations && insights.recommendations.length > 0 ? (
              <div className="recommendations">
                {insights.recommendations.map((rec, idx) => (
                  <div key={idx} className="recommendation-card">
                    <div className="rec-priority" style={{
                      background: rec.priority === 'critical' ? '#ff3333' :
                                 rec.priority === 'high' ? '#ffa500' : '#ffd700'
                    }}>
                      {rec.priority?.toUpperCase()}
                    </div>
                    <div className="rec-content">
                      <h4>{rec.action}</h4>
                      <p>{rec.rationale}</p>
                      {rec.affected_isps && (
                        <div className="affected-isps">
                          <strong>ISPs Affected:</strong> {rec.affected_isps.join(', ')}
                        </div>
                      )}
                      {rec.estimated_impact && (
                        <div className="estimated-impact">
                          <strong>Estimated Impact:</strong> {rec.estimated_impact}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#999' }}>No specific recommendations at this time.</p>
            )}
          </div>

          {/* Risk Summary */}
          {insights.risk_summary && (
            <div className="analytics-card full-width">
              <h3>⚠️ Risk Summary</h3>
              <div className="risk-summary">
                <div className="risk-item">
                  <div className="risk-label">Critical Issues</div>
                  <div className="risk-value" style={{ color: '#ff3333' }}>
                    {insights.risk_summary.critical_issues || 0}
                  </div>
                </div>
                <div className="risk-item">
                  <div className="risk-label">High Priority Items</div>
                  <div className="risk-value" style={{ color: '#ffa500' }}>
                    {insights.risk_summary.high_priority || 0}
                  </div>
                </div>
                <div className="risk-item">
                  <div className="risk-label">Upcoming Deadlines</div>
                  <div className="risk-value" style={{ color: '#ffd700' }}>
                    {insights.risk_summary.upcoming_deadlines || 0}
                  </div>
                </div>
                <div className="risk-item">
                  <div className="risk-label">Days to Most Urgent</div>
                  <div className="risk-value">
                    {insights.risk_summary.days_to_most_urgent ?? 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Strategic Overview */}
          {insights.strategic_overview && (
            <div className="analytics-card full-width">
              <h3>🎯 Strategic Overview</h3>
              <div style={{
                background: '#f5f5f5',
                padding: '20px',
                borderRadius: '8px',
                lineHeight: '1.6'
              }}>
                {insights.strategic_overview}
              </div>
            </div>
          )}

          {/* Opportunity Areas */}
          {insights.opportunity_areas && insights.opportunity_areas.length > 0 && (
            <div className="analytics-card full-width">
              <h3>🚀 Opportunity Areas</h3>
              <div className="opportunities">
                {insights.opportunity_areas.map((opp, idx) => (
                  <div key={idx} className="opportunity-card">
                    <h4>{opp.area}</h4>
                    <p>{opp.description}</p>
                    {opp.potential_benefit && (
                      <div style={{ color: '#2e7d32', fontSize: '12px' }}>
                        <strong>Potential Benefit:</strong> {opp.potential_benefit}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
